/**
 * Runtime capability registry.
 *
 * Single source of truth for what the current platform supports.
 * UI components should check these flags rather than inlining
 * `window.electron`, `isInTauriShell()`, or UA sniffing.
 *
 * Capabilities are computed once at module load and are immutable for
 * the lifetime of the page (no hot-reload of hardware capabilities).
 */

import { isInTauriShell, isMobilePlatform, getPlatformFallback } from './detector'

export interface AppCapabilities {
  /** Can silently print to a connected printer (Electron desktop only) */
  silentPrint: boolean
  /** Can enumerate connected printers (Electron desktop only) */
  printerDiscovery: boolean
  /** Can connect to USB/gphoto2 DSLR cameras (Electron desktop only) */
  usbCamera: boolean
  /** Can save files to local disk / gallery */
  fileDownload: boolean
  /** Can configure app to auto-launch at OS startup (Electron desktop only) */
  autoLaunch: boolean
  /** Has a tunnel URL feature for remote printing (Electron desktop only) */
  tunnelUrl: boolean
  /** Can enter/exit window fullscreen (same kiosk semantics on desktop, phone browser, and Tauri webview). */
  manageFullscreen: boolean
  /** Platform supports a physical back button / swipe-back gesture (Android) */
  hardwareBack: boolean
  /** Platform needs safe-area inset CSS padding (iOS notch / Android gesture bar) */
  safeAreaInsets: boolean
  /** Is a mobile platform (phone or tablet) */
  isMobile: boolean
  /** ESC/POS thermal receipt print (Android Tauri bahelastruk) */
  thermalPrint: boolean
  thermalSilent: boolean
  thermalDiscovery: boolean
}

function computeCapabilities(): AppCapabilities {
  const isElectron = typeof window !== 'undefined' && typeof window.electron !== 'undefined'
  const isTauri = isInTauriShell()
  const isMobile = isMobilePlatform()
  const platform = getPlatformFallback()
  const isAndroidTauri = isTauri && platform === 'android'

  return {
    silentPrint: isElectron,
    printerDiscovery: isElectron,
    usbCamera: isElectron,
    fileDownload: true,
    autoLaunch: isElectron,
    tunnelUrl: isElectron,
    manageFullscreen: true,
    hardwareBack: platform === 'android',
    safeAreaInsets: isMobile || isTauri,
    isMobile,
    thermalPrint: isAndroidTauri,
    thermalSilent: isAndroidTauri,
    thermalDiscovery: isAndroidTauri,
  }
}

// Singleton — evaluated once at module import time.
export const capabilities: AppCapabilities = computeCapabilities()
